/**
 * Age Verification Service
 *
 * Centralized service for managing age verification across Liquid and TypeScript components.
 * Provides a simple API for requesting verification and handling callbacks.
 */
import { ThemeEvents } from '@theme/events';

const AGE_VERIFIED_KEY = 'age_verified';

function getCookie(name) {
  return document.cookie
    .split('; ')
    .find((r) => r.startsWith(name + '='))
    ?.split('=')[1];
}

function setCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${value}; expires=${expires}; path=/`;
}

function removeCookie(name) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
}

class AgeVerificationService {
  /** @type {(() => void) | null} */
  #pendingCallback = null;

  /**
   * Checks if age is verified in cookies
   * @returns {boolean} True if age is verified
   */
  isVerified() {
    return getCookie(AGE_VERIFIED_KEY) === 'true';
  }

  /**
   * Requests age verification. If already verified, calls callback immediately.
   * If not verified, shows modal and calls callback after verification.
   * @param {() => void} callback - Function to call after verification
   */
  requestVerification(callback) {
    if (this.isVerified()) {
      callback();
      return;
    }

    this.#pendingCallback = callback;

    document.dispatchEvent(new CustomEvent('age-verification:show'));
  }

  /**
   * Called when user verifies their age via checkbox
   */
  onVerified() {
    setCookie(AGE_VERIFIED_KEY, 'true', 7);

    if (this.#pendingCallback) {
      this.#pendingCallback();
      this.#pendingCallback = null;
    }

    this.#syncToCartAttributes().catch((error) => {
      console.warn('Background cart sync failed:', error);
    });
  }

  clearVerification() {
    removeCookie(AGE_VERIFIED_KEY);
    this.#pendingCallback = null;
  }

  async #syncToCartAttributes() {
    try {
      await fetch(`${Theme.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            'age-verified': true,
          },
        }),
      });
    } catch (error) {
      console.warn('Failed to sync to cart attributes:', error);
    }
  }
}

const ageVerificationService = new AgeVerificationService();

// Export to window for TypeScript access
// @ts-ignore - Adding to window object
window.ageVerificationService = ageVerificationService;

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (event instanceof CustomEvent && event.detail?.data) {
    const { data } = event.detail;
    if (data.itemCount === 0) {
      ageVerificationService.clearVerification();
    }
  }
});

export default ageVerificationService;
